(function bandFinderMatcherModule(globalScope) {
  "use strict";

  function normalize(value) {
    return String(value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/([a-z])(\d)/g, "$1 $2")
      .replace(/(\d)([a-z])/g, "$1 $2")
      .replace(/\b(\d+)\s+mm\b/g, "$1mm")
      .trim();
  }

  function compact(value) {
    return normalize(value).replace(/\s+/g, "");
  }

  function normalizeCode(value) {
    return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function withinOneEdit(left, right) {
    if (left === right) return true;
    if (left.length === right.length) {
      for (let index = 0; index < left.length - 1; index += 1) {
        if (left.slice(0, index) + left[index + 1] + left[index] + left.slice(index + 2) === right) return true;
      }
    }
    if (Math.abs(left.length - right.length) > 1) return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < left.length && j < right.length) {
      if (left[i] === right[j]) {
        i += 1;
        j += 1;
      } else {
        edits += 1;
        if (edits > 1) return false;
        if (left.length > right.length) i += 1;
        else if (right.length > left.length) j += 1;
        else {
          i += 1;
          j += 1;
        }
      }
    }
    return edits + (left.length - i) + (right.length - j) <= 1;
  }

  function codeWindows(text) {
    const chunks = String(text || "").toUpperCase().match(/[A-Z0-9]+/g) || [];
    const windows = new Set();
    for (let start = 0; start < chunks.length; start += 1) {
      let candidate = "";
      for (let size = 1; size <= 3 && start + size <= chunks.length; size += 1) {
        candidate += chunks[start + size - 1];
        windows.add(candidate);
      }
    }
    return windows;
  }

  function extractCandidateCodes(text, watches) {
    const windows = codeWindows(text);
    const matches = [];
    const seen = new Set();
    for (const watch of watches || []) {
      for (const stored of watch.confirmedCodes || []) {
        const code = normalizeCode(stored);
        if (code.length < 4 || seen.has(code)) continue;
        const found = windows.has(code);
        if (found) {
          seen.add(code);
          matches.push(stored);
        }
      }
    }
    return matches;
  }

  function makeResult(state, confidence, matches, selected) {
    return { state, confidence, matches, selected };
  }

  function classifySelection(watch, confidence) {
    if (!watch) return makeResult("no_match", "none", [], null);
    if (watch.verification === "under_review") {
      return makeResult("under_review", confidence, [watch], watch);
    }
    if (/^NOT STOCKED\b/i.test(watch.connectorId || "")) {
      return makeResult("no_stock", confidence, [watch], watch);
    }
    return makeResult("confirmed", confidence, [watch], watch);
  }

  function createMatcher(watches) {
    const source = Array.isArray(watches) ? watches.slice() : [];
    const prepared = source.map((watch) => {
      const aliases = watch.aliases || [];
      const caseSize = watch.caseSize || "";
      const candidates = [
        `${watch.brand} ${watch.model}`,
        `${watch.brand} ${watch.model} ${caseSize}`,
        `${watch.model} ${caseSize}`,
        watch.model,
        ...aliases,
        ...aliases.map((alias) => `${watch.brand} ${alias}`),
        ...aliases.map((alias) => `${alias} ${caseSize}`),
      ].map(normalize).filter(Boolean);
      const words = new Set(normalize([
        watch.brand,
        watch.model,
        caseSize,
        ...aliases,
      ].join(" ")).split(" ").filter(Boolean));
      return { watch, candidates, compacts: candidates.map(compact), words };
    });
    const byId = new Map(source.map((watch) => [watch.id, watch]));
    const bySlug = new Map(source.map((watch) => [watch.slug, watch]));
    const familySlug = (watch) => `family-${normalize(`${watch.brand} ${watch.model}`).replace(/\s+/g, "-")}`;
    const families = new Map();
    for (const watch of source) {
      const key = familySlug(watch);
      if (!families.has(key)) families.set(key, []);
      families.get(key).push(watch);
    }

    // A family answer describes all listed sizes, never a guessed individual watch.
    function sharedFitting(hits, confidence) {
      if (hits.length < 2) return null;
      const first = hits[0];
      const slug = familySlug(first);
      if (families.get(slug).length !== hits.length) return null;
      const safe = hits.every((watch) =>
        watch.brand === first.brand && watch.model === first.model &&
        watch.verification === "confirmed" && watch.caseSize &&
        watch.connectorId === first.connectorId && !/^NOT STOCKED\b/i.test(watch.connectorId) &&
        watch.collectionUrl === first.collectionUrl &&
        JSON.stringify(watch.warnings || []) === JSON.stringify(first.warnings || [])
      );
      const sizes = [...new Set(hits.map((watch) => watch.caseSize))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      if (!safe || sizes.length !== hits.length) return null;
      const family = {
        ...first, id: slug, slug, caseSize: null, compatibleCaseSizes: sizes,
        confirmedCodes: [], casebackHint: null, evidenceUrls: [], verifiedAt: null,
      };
      return makeResult("confirmed", confidence, hits, family);
    }

    function select(id) {
      return classifySelection(byId.get(id), "confirmed_selection");
    }

    function match(input) {
      const query = normalize(input).split(" ").filter((word) => !["my", "for", "band", "bands", "strap", "straps"].includes(word)).join(" ");
      if (!query) return makeResult("no_match", "none", [], null);

      const foundCodes = extractCandidateCodes(input, source).map(normalizeCode);
      if (foundCodes.length) {
        const hits = source.filter((watch) =>
          (watch.confirmedCodes || []).some((code) => foundCodes.includes(normalizeCode(code)))
        );
        if (hits.length === 1) return classifySelection(hits[0], "exact_code");
        if (hits.length > 1) return makeResult("confirmation_required", "exact_code", hits, null);
      }

      const queryCompact = compact(query);
      if (queryCompact.length < 2 || !/[a-z]/.test(query)) return makeResult("no_match", "none", [], null);
      const queryWords = query.split(" ").filter(Boolean);
      const ranked = (entries) => entries.slice().sort((left, right) => {
        const score = (entry) => queryWords.reduce((total, word) => total + (entry.words.has(word) ? 4 : 1), 0)
          - Math.min(...entry.candidates.map((candidate) => candidate.split(" ").length)) * 0.01;
        return score(right) - score(left) || `${left.watch.brand} ${left.watch.model}`.localeCompare(`${right.watch.brand} ${right.watch.model}`, undefined, { numeric: true });
      });
      const broad = ranked(prepared.filter((entry) => queryWords.every((word) =>
        entry.words.has(word) || (!/\d/.test(word) && word.length >= 2 && [...entry.words].some((candidate) => candidate.startsWith(word)))
      )));
      const exact = prepared.filter((entry) =>
        entry.candidates.includes(query) || entry.compacts.includes(queryCompact)
      );
      // A family alias such as "Venu" must not silently select generation one.
      if (exact.length === 1 && queryCompact.length >= 4 && (/\d/.test(query) || broad.length <= 1)) return classifySelection(exact[0].watch, "exact_name");
      if (exact.length > 1) {
        const hits = exact.map((entry) => entry.watch);
        const fullModelName = hits.every((watch) =>
          [watch.model, `${watch.brand} ${watch.model}`].some((name) => compact(name) === queryCompact)
        );
        return (fullModelName && sharedFitting(hits, "exact_name")) || makeResult("confirmation_required", "exact_name", hits, null);
      }

      if (broad.length === 1) {
        return makeResult("suggested", "suggestion", [broad[0].watch], null);
      }
      if (broad.length > 1) {
        return makeResult("confirmation_required", "exact_name", broad.map((entry) => entry.watch), null);
      }

      const fuzzy = ranked(prepared.filter((entry) => queryWords.every((word) => {
        if (entry.words.has(word)) return true;
        if (word.length < 4 || /\d/.test(word)) return false;
        return [...entry.words].some((candidate) => !/\d/.test(candidate) && candidate.length >= 4 && withinOneEdit(candidate, word));
      })));
      if (fuzzy.length) {
        return makeResult("suggested", "suggestion", fuzzy.map((entry) => entry.watch), null);
      }
      return makeResult("no_match", "none", [], null);
    }

    function resolveSlug(slug) {
      const value = String(slug || "");
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
        return makeResult("no_match", "none", [], null);
      }
      if (families.has(value)) {
        const members = families.get(value);
        return sharedFitting(members, "confirmed_selection") || makeResult("confirmation_required", "confirmed_selection", members, null);
      }
      return classifySelection(bySlug.get(value), "confirmed_selection");
    }

    return { match, select, resolveSlug };
  }

  function serializeShareState(watch) {
    const slug = String(watch && watch.slug || "");
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return "";
    return `?watch=${encodeURIComponent(slug)}`;
  }

  const api = {
    createMatcher,
    extractCandidateCodes,
    serializeShareState,
  };

  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) globalScope.NBBBandFinderMatcher = api;
}(typeof globalThis !== "undefined" ? globalThis : this));


(function bandFinderAnalyticsModule(globalScope) {
  "use strict";

  const EVENTS = new Set([
    "band_finder_view",
    "band_finder_input_selected",
    "band_finder_scan_started",
    "band_finder_scan_completed",
    "band_finder_match_state",
    "band_finder_result_shared",
    "band_finder_collection_clicked",
  ]);

  const VALUES = Object.freeze({
    input_mode: new Set(["photo", "text", "shared_url"]),
    outcome: new Set([
      "confirmed",
      "confirmation_required",
      "suggested",
      "under_review",
      "no_stock",
      "no_match",
      "error",
    ]),
    confidence: new Set([
      "exact_code",
      "exact_name",
      "confirmed_selection",
      "suggestion",
      "none",
    ]),
    result_count_bucket: new Set(["0", "1", "2_3", "4_plus"]),
    error_code: new Set([
      "invalid_image",
      "too_large",
      "rate_limited",
      "provider_unavailable",
      "no_text",
      "none",
    ]),
  });

  function validPayload(params) {
    if (!params || typeof params !== "object" || Array.isArray(params)) return false;
    return Object.entries(params).every(([key, value]) =>
      Object.prototype.hasOwnProperty.call(VALUES, key) && VALUES[key].has(value)
    );
  }

  function createAnalyticsAdapter(sink) {
    const publish = typeof sink === "function" ? sink : function noop() {};
    function emit(eventName, params) {
      if (!EVENTS.has(eventName) || !validPayload(params)) return false;
      const safeParams = Object.freeze({ ...params });
      try {
        publish(eventName, safeParams);
        return true;
      } catch (_error) {
        return false;
      }
    }
    return Object.freeze({ emit });
  }

  const api = { createAnalyticsAdapter };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) globalScope.NBBBandFinderAnalytics = api;
}(typeof globalThis !== "undefined" ? globalThis : this));


(function bandFinderPhotoModule(globalScope) {
  "use strict";

  const MAX_EDGE = 1600;
  const MAX_BYTES = 2_000_000;
  const SUPPORTED_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
  ]);
  const HEIF_TYPES = new Set(["image/heic", "image/heif"]);
  const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5];

  function photoError(code) {
    const error = new Error(code);
    error.code = code;
    return error;
  }

  function validatePhoto(file) {
    if (!file || typeof file.size !== "number" || file.size <= 0) {
      return { ok: false, code: "invalid_image" };
    }
    if (!SUPPORTED_TYPES.has(String(file.type || "").toLowerCase())) {
      return { ok: false, code: "unsupported_image" };
    }
    return { ok: true };
  }

  function outputDimensions(width, height, orientation) {
    const rotated = orientation === 6 || orientation === 8;
    const uprightWidth = rotated ? height : width;
    const uprightHeight = rotated ? width : height;
    const scale = Math.min(1, MAX_EDGE / Math.max(uprightWidth, uprightHeight));
    return {
      width: Math.max(1, Math.round(uprightWidth * scale)),
      height: Math.max(1, Math.round(uprightHeight * scale)),
    };
  }

  async function blobToDataUrl(blob) {
    if (typeof FileReader !== "undefined") {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(photoError("invalid_image"));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }
    if (typeof Buffer !== "undefined") {
      const bytes = Buffer.from(await blob.arrayBuffer());
      return `data:${blob.type};base64,${bytes.toString("base64")}`;
    }
    throw photoError("invalid_image");
  }

  async function preparePhoto(file, browser) {
    const validity = validatePhoto(file);
    if (!validity.ok) throw photoError(validity.code);
    if (!browser || typeof browser.decodeImage !== "function" || typeof browser.createCanvas !== "function") {
      throw photoError("invalid_image");
    }

    let decoded;
    try {
      decoded = await browser.decodeImage(file);
    } catch (_error) {
      throw photoError(HEIF_TYPES.has(String(file.type).toLowerCase()) ? "unsupported_image" : "invalid_image");
    }
    if (!decoded || !(decoded.width > 0) || !(decoded.height > 0)) {
      throw photoError("invalid_image");
    }

    const orientation = [1, 3, 6, 8].includes(decoded.orientation) ? decoded.orientation : 1;
    const dimensions = outputDimensions(decoded.width, decoded.height, orientation);
    const canvas = browser.createCanvas(dimensions.width, dimensions.height);
    if (!canvas || typeof canvas.draw !== "function" || typeof canvas.toBlob !== "function") {
      if (typeof decoded.close === "function") decoded.close();
      throw photoError("invalid_image");
    }

    try {
      canvas.draw(decoded.source, {
        orientation,
        sourceWidth: decoded.width,
        sourceHeight: decoded.height,
        width: dimensions.width,
        height: dimensions.height,
      });
    } finally {
      if (typeof decoded.close === "function") decoded.close();
    }

    let blob = null;
    for (const quality of QUALITY_STEPS) {
      blob = await canvas.toBlob("image/jpeg", quality);
      if (!blob || blob.type !== "image/jpeg") throw photoError("invalid_image");
      if (blob.size <= MAX_BYTES) break;
    }
    if (!blob || blob.size > MAX_BYTES) throw photoError("too_large");

    const dataUrl = await blobToDataUrl(blob);
    const previewUrl = browser.createObjectURL(blob);
    let disposed = false;
    function dispose() {
      if (disposed) return;
      disposed = true;
      if (typeof browser.revokeObjectURL === "function") browser.revokeObjectURL(previewUrl);
    }

    return {
      blob,
      dataUrl,
      width: dimensions.width,
      height: dimensions.height,
      previewUrl,
      dispose,
    };
  }

  const api = { MAX_EDGE, MAX_BYTES, preparePhoto, validatePhoto };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) globalScope.NBBBandFinderPhoto = api;
}(typeof globalThis !== "undefined" ? globalThis : this));


(function bandFinderOcrClientModule(globalScope) {
  "use strict";

  const PUBLIC_ERRORS = new Set([
    "invalid_image",
    "too_large",
    "rate_limited",
    "provider_unavailable",
    "no_text",
  ]);

  function exactKeys(value, expected) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const actual = Object.keys(value).sort();
    const wanted = expected.slice().sort();
    return actual.length === wanted.length && actual.every((key, index) => key === wanted[index]);
  }

  function parsePayload(payload) {
    if (exactKeys(payload, ["ok", "text", "provider"]) && payload.ok === true) {
      if (payload.provider !== "google_vision" || typeof payload.text !== "string") {
        return { ok: false, code: "provider_unavailable" };
      }
      const text = payload.text.trim();
      return text
        ? { ok: true, text, provider: "google_vision" }
        : { ok: false, code: "no_text" };
    }
    if (
      exactKeys(payload, ["ok", "code"])
      && payload.ok === false
      && PUBLIC_ERRORS.has(payload.code)
    ) {
      return { ok: false, code: payload.code };
    }
    return { ok: false, code: "provider_unavailable" };
  }

  function createOcrClient(options) {
    const endpoint = String(options && options.endpoint || "");
    const fetchImpl = options && options.fetchImpl;

    async function read(dataUrl, signal) {
      const match = /^data:image\/jpeg;base64,([A-Za-z0-9+/]+={0,2})$/.exec(String(dataUrl || ""));
      if (!match) return { ok: false, code: "invalid_image" };
      if (!endpoint || typeof fetchImpl !== "function") {
        return { ok: false, code: "provider_unavailable" };
      }
      try {
        const response = await fetchImpl(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image: match[1] }),
          credentials: "omit",
          signal,
        });
        const payload = await response.json();
        return parsePayload(payload);
      } catch (_error) {
        return { ok: false, code: "provider_unavailable" };
      }
    }

    return Object.freeze({ read });
  }

  const api = { createOcrClient };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) globalScope.NBBBandFinderOcrClient = api;
}(typeof globalThis !== "undefined" ? globalThis : this));


(function bandFinderControllerModule(globalScope) {
  "use strict";

  const LABELS = Object.freeze({
    confirmed: "Your watch",
    confirmation_required: "Confirm your watch",
    suggested: "Suggested matches",
    under_review: "Fitting unavailable",
    no_stock: "No band stocked",
    no_match: "No reliable match",
  });

  const ERROR_MESSAGES = Object.freeze({
    invalid_image: "We could not read that image. Try a clear JPG, PNG, or WebP photo.",
    unsupported_image: "This browser could not open that HEIC or HEIF image. Try a screenshot or JPEG instead.",
    too_large: "That image is too large. Try a smaller photo.",
    rate_limited: "Photo lookup is busy right now. Search by watch name or model code instead.",
    provider_unavailable: "Photo lookup is temporarily unavailable. You can retry or search manually.",
    no_text: "We could not find a model code in that photo. Try another angle or search manually.",
  });

  function resultCountBucket(count) {
    if (count <= 0) return "0";
    if (count === 1) return "1";
    if (count <= 3) return "2_3";
    return "4_plus";
  }

  function createBandFinderController(dependencies) {
    const matcher = dependencies.matcher;
    const serializeShareState = dependencies.serializeShareState;
    const preparePhoto = dependencies.preparePhoto;
    const ocrClient = dependencies.ocrClient;
    const analytics = dependencies.analytics || { emit() {} };
    const navigatorApi = dependencies.navigator || {};
    const historyApi = dependencies.history || {};
    const locationApi = dependencies.location || { origin: "", pathname: "", search: "" };

    let root;
    let elements;
    let inputMode = "text";
    let currentResult = null;
    let currentPhoto = null;
    let currentAbort = null;
    let lastFile = null;
    let scanGeneration = 0;

    function touchTarget(control) {
      if (control && control.classList) control.classList.add("wbf-touch-target");
      return control;
    }

    function makeElement(tag, attributes, text) {
      const element = root.ownerDocument.createElement(tag);
      for (const [name, value] of Object.entries(attributes || {})) {
        if (name === "className") element.className = value;
        else if (name.startsWith("data-")) element.setAttribute(name, value === true ? "" : String(value));
        else element.setAttribute(name, String(value));
      }
      if (text !== undefined) element.textContent = text;
      return element;
    }

    function watchName(watch) {
      let name = watch.model || "";
      if (!name.toLowerCase().startsWith(`${watch.brand.toLowerCase()} `)) name = `${watch.brand} ${name}`;
      if (watch.caseSize && !name.toLowerCase().includes(watch.caseSize.toLowerCase())) name += ` ${watch.caseSize}`;
      return name;
    }

    function clearError() {
      elements.error.hidden = true;
      elements.error.textContent = "";
    }

    function setStatus(message, quiet = false) {
      elements.status.textContent = message || "";
      elements.status.classList.toggle("wbf-sr-only", quiet);
    }

    function updateLocation(watch) {
      if (!historyApi || typeof historyApi.replaceState !== "function") return;
      const suffix = watch ? serializeShareState(watch) : "";
      try { historyApi.replaceState(null, "", `${locationApi.pathname}${suffix}`); }
      catch (_error) { /* Local previews may disallow history changes; search still works. */ }
    }

    function emitMatchState(result, mode, errorCode) {
      analytics.emit("band_finder_match_state", {
        input_mode: mode,
        outcome: result ? result.state : "error",
        confidence: result ? result.confidence : "none",
        result_count_bucket: resultCountBucket(result ? result.matches.length : 0),
        error_code: errorCode || "none",
      });
    }

    function appendWarnings(container, watch) {
      const notes = [...new Set(watch.warnings || [])];
      // Compare only known related records, never infer a fitting from a name.
      if (watch.verification === "confirmed") {
        const different = (watch.relatedIds || []).map(id => matcher.select(id).selected)
          .filter(other => other && other.id !== watch.id && other.brand === watch.brand &&
            other.verification === "confirmed" && other.connectorId !== watch.connectorId);
        const comparisons = [...new Set(different.map(other => `${watchName(other)} uses ${fittingDetails(other).title}`))].slice(0, 2);
        comparisons.forEach(comparison => notes.push(`Similar name, different fitting: ${comparison}.`));
      }
      if (!notes.length) return;
      const section = makeElement("section", { className: "wbf-fit-notes", "data-fit-notes": true, "aria-label": "Good to know" });
      section.append(makeElement("h3", {}, "Good to know"));
      const list = makeElement("ul", { className: "wbf-result__warnings" });
      for (const warning of notes) {
        const item = makeElement("li");
        const separator = warning.indexOf(": ");
        if (separator > 0 && separator < 65) {
          item.append(makeElement("strong", {}, warning.slice(0, separator + 1)));
          item.append(root.ownerDocument.createTextNode(` ${warning.slice(separator + 2)}`));
        } else {
          item.append(root.ownerDocument.createTextNode(warning));
        }
        list.append(item);
      }
      section.append(list);
      container.append(section);
    }

    function appendShareButton(container) {
      const button = touchTarget(makeElement("button", {
        type: "button",
        className: "wbf-share-button",
        "data-share-result": true,
      }, "Share this fit"));
      const icon = makeElement('span', { 'aria-hidden': 'true', className: 'wbf-share-icon' });
      icon.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 16V3m-4 4 4-4 4 4M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"/></svg>';
      button.prepend(icon);
      button.addEventListener("click", () => { void share(); });
      container.append(button);
    }

    function fittingDetails(watch) {
      const connector = watch.connectorId.replace(/^NOT STOCKED\s*[—-]\s*/i, "");
      const quickRelease = /^Quick Release (\d+)mm$/i.exec(connector);
      if (quickRelease) return {
        title: `${quickRelease[1]}mm quick release`,
        note: `${quickRelease[1]}mm is the attachment width—not the length around your wrist. Choose the band length to suit your wrist size.`,
        quickRelease: true,
      };
      const quickFit = /^Garmin QuickFit (\d+)$/.exec(connector);
      if (quickFit) return { title: `${quickFit[1]}mm Garmin QuickFit`, note: "Choose the QuickFit latch connection, not a standard quick-release band." };
      return { title: connector, note: "" };
    }

    function appendInstallationHelp(container, watch) {
      if (!fittingDetails(watch).quickRelease) return;
      const details = makeElement("details", { className: "wbf-installation", "data-installation-help": true });
      details.append(makeElement("summary", {}, "How to change the band"));
      const samsungOneClick = watch.brand === "Samsung" && /^Galaxy Watch 6(?: Classic)?$/.test(watch.model);
      const steps = samsungOneClick ? [
        "If your current band has Samsung’s One-Click button, hold the button underneath and detach the band. For a band with a sliding release lever, slide the lever inward to detach it.",
        "For a standard quick-release replacement, place one end of the spring bar into the watch lug. Hold the small lever back, align the other end, then release it into place.",
        "For a One-Click replacement, hold its button while aligning the spring-bar ends with the lug holes, then release. Gently tug each band to check that both ends are secured before wearing the watch.",
      ] : [
        "For a band with a quick-release lever, slide the small lever underneath inward and lift the band away from the watch.",
        "Place one end of the replacement band’s spring bar into the watch lug. Hold the lever back, align the other end, then release it into place.",
        "Gently tug each band to check that both ends are secured. If your current band uses a different release mechanism, use the watch’s removal instructions rather than forcing it.",
      ];
      const list = makeElement("ol");
      steps.forEach((step) => list.append(makeElement("li", {}, step)));
      const content = makeElement("div", { "data-accordion-content": true });
      content.append(list);
      details.append(content);
      container.append(details);
    }

    function renderSelected(container, result) {
      const watch = result.selected;
      const heading = makeElement("h2", {
        tabindex: "-1",
        "data-result-heading": true,
      }, watchName(watch));
      container.append(heading);
      if (watch.compatibleCaseSizes) {
        const sizes = makeElement("div", { className: "wbf-case-sizes", "data-case-sizes": true });
        sizes.append(makeElement("span", { className: "wbf-case-sizes__label" }, "Case sizes"));
        for (const size of watch.compatibleCaseSizes) {
          sizes.append(makeElement("span", { className: "wbf-case-size-pill", "data-case-size-pill": true }, size));
        }
        sizes.append(makeElement("span", { className: "wbf-case-sizes__note" }, `${watch.compatibleCaseSizes.length === 2 ? "Both" : "All listed"} sizes use the same band fitting.`));
        container.append(sizes);
      }

      if (result.state === "confirmed" || result.state === "no_stock") {
        const fitting = makeElement("div", {
          className: "wbf-fitting",
          "data-fitting": true,
        });
        const description = fittingDetails(watch);
        fitting.append(makeElement("span", { className: "wbf-fitting__eyebrow" }, "Your band fitting"));
        fitting.append(makeElement("strong", {}, description.title));
        if (description.note) fitting.append(makeElement("p", {}, description.note));
        container.append(fitting);
      }

      if (result.state === "under_review") {
        container.append(makeElement("p", {}, "We don’t have a band fitting to show for this version. Search another model or contact us for help."));
        container.append(makeElement("a", { href: "/pages/contact", className: "wbf-text-link" }, "Get help finding a band"));
      } else if (result.state === "no_stock") {
        container.append(makeElement("p", {}, "We identified the watch, but we do not currently stock a compatible band."));
      }

      if (result.state === "confirmed" && watch.collectionUrl) {
        const localPreview = /^(?:null|http:\/\/(?:127\.0\.0\.1|localhost)(?::\d+)?)$/.test(locationApi.origin);
        const link = touchTarget(makeElement("a", {
          href: localPreview ? `https://nothingbutbands.com${watch.collectionUrl}` : watch.collectionUrl,
          className: "wbf-button wbf-button--primary",
          "data-collection-cta": true,
        }, "View compatible bands"));
        link.addEventListener("click", () => {
          analytics.emit("band_finder_collection_clicked", {
            input_mode: inputMode,
            outcome: "confirmed",
            confidence: result.confidence,
          });
        });
        container.append(link);
      }

      appendWarnings(container, watch);

      const actions = makeElement("div", { className: "wbf-result-actions" });
      const change = touchTarget(makeElement("button", { type: "button", className: "wbf-result-action", "data-change-watch": true }, "Change watch"));
      change.addEventListener("click", () => { setInputMode("text"); reset(); });
      actions.append(change);
      appendShareButton(actions);
      if (result.state === "confirmed" || result.state === "no_stock") appendInstallationHelp(container, watch);
      container.append(actions);
    }

    function chooseWatch(id) {
      const selected = matcher.select(id);
      renderResult(selected, inputMode, true);
    }

    function renderChoices(container, result, inlineChoices) {
      const guidance = result.state === "confirmation_required"
        ? "Choose the exact size or version before we show a band fit."
        : "These are close matches. Choose yours to confirm the fit.";
      if (!inlineChoices) container.append(makeElement("p", {}, guidance));
      const list = makeElement("div", { className: "wbf-choices" });
      let shown = 0;
      const more = touchTarget(makeElement("button", { type: "button", className: "wbf-button wbf-button--secondary", "data-more-matches": true }, "Show more matches"));
      function addBatch() {
      for (const watch of result.matches.slice(shown, shown + 8)) {
        const button = touchTarget(makeElement("button", {
          type: "button",
          className: "wbf-choice",
          "data-watch-choice": true,
        }));
        button.append(makeElement("strong", {}, watchName(watch)));
        if (watch.casebackHint) button.append(makeElement("span", {}, watch.casebackHint));
        button.addEventListener("click", () => chooseWatch(watch.id));
        button.addEventListener("keydown", (event) => {
          if (!["ArrowDown", "ArrowUp", "Escape"].includes(event.key)) return;
          event.preventDefault();
          const target = event.key === "ArrowDown" ? button.nextElementSibling : event.key === "ArrowUp" ? button.previousElementSibling : null;
          (target || elements.textInput).focus();
        });
        list.append(button);
      }
      shown = Math.min(shown + 8, result.matches.length);
      more.hidden = shown >= result.matches.length;
      more.textContent = `Show more (${result.matches.length - shown} remaining)`;
      }
      addBatch();
      more.addEventListener("click", () => {
        const previous = shown;
        addBatch();
        list.children[previous]?.focus();
      });
      container.append(list);
      container.append(more);
    }

    function renderNoMatch(container) {
      const heading = makeElement("h2", {
        tabindex: "-1",
        "data-result-heading": true,
      }, "We could not identify that watch yet");
      container.append(heading);
      container.append(makeElement("p", {}, "Try the model code from the back of the case, or include the brand and case size."));
    }

    function renderResult(result, mode, shouldFocus) {
      currentResult = result;
      inputMode = mode || inputMode;
      clearError();
      setStatus("");
      elements.results.replaceChildren();
      root.dataset.state = result.state;

      const inlineChoices = inputMode === "text" && !result.selected && result.matches.length > 0;
      const card = makeElement("article", { className: `wbf-result wbf-result--${result.state}${inlineChoices ? " wbf-result--choices" : ""}` });
      if (!inlineChoices) card.append(makeElement("p", {
        className: "wbf-result__label",
        "data-result-label": true,
      }, LABELS[result.state]));

      if (result.selected) renderSelected(card, result);
      else if (result.matches.length) renderChoices(card, result, inlineChoices);
      else renderNoMatch(card);
      elements.results.append(card);
      if (elements.reset) elements.reset.hidden = Boolean(result.selected);
      setStatus(result.selected ? `Found ${watchName(result.selected)}.` : result.matches.length ? `${result.matches.length} watches found. Choose your model.` : "No matching watches yet.", Boolean(result.selected) || inlineChoices);

      if (result.selected) updateLocation(result.selected);
      else updateLocation(null);
      emitMatchState(result, inputMode);

      if (shouldFocus) {
        const target = result.matches.length && !result.selected
          ? elements.results.querySelector("[data-watch-choice]")
          : elements.results.querySelector("[data-result-heading]");
        if (target) target.focus();
      }
    }

    function search(query, options) {
      scanGeneration += 1;
      if (currentAbort) currentAbort.abort();
      currentAbort = null;
      const result = matcher.match(query);
      const shouldFocus = !options || options.shouldFocus !== false;
      renderResult(result, (options && options.inputMode) || "text", shouldFocus);
      return result;
    }

    function setInputMode(mode, shouldFocus) {
      if (mode !== "photo" && mode !== "text") return;
      if (mode === "photo" && !elements.photoInput) return;
      if (mode === "text" && currentAbort) {
        scanGeneration += 1;
        currentAbort.abort();
        currentAbort = null;
        root.dataset.state = "idle";
        setStatus("");
      }
      inputMode = mode;
      const isPhoto = mode === "photo";
      if (elements.photoPanel) elements.photoPanel.hidden = !isPhoto;
      elements.textPanel.hidden = isPhoto;
      for (const tab of elements.tabs) {
        const selected = tab.dataset.inputMode === mode;
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.setAttribute("tabindex", selected ? "0" : "-1");
      }
      analytics.emit("band_finder_input_selected", { input_mode: mode });
      if (shouldFocus) {
        const tab = elements.tabs.find((candidate) => candidate.dataset.inputMode === mode);
        if (tab) tab.focus();
      }
    }

    function renderScanError(code) {
      const safeCode = Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, code)
        ? code
        : "provider_unavailable";
      const analyticsCode = safeCode === "unsupported_image" ? "invalid_image" : safeCode;
      root.dataset.state = "error";
      setStatus("");
      elements.results.replaceChildren();
      elements.error.replaceChildren();
      elements.error.hidden = false;
      elements.error.append(makeElement("p", {}, ERROR_MESSAGES[safeCode]));
      const retry = touchTarget(makeElement("button", {
        type: "button",
        className: "wbf-button wbf-button--secondary",
        "data-retry": true,
      }, "Try the photo again"));
      retry.addEventListener("click", () => { if (lastFile) void scan(lastFile); });
      elements.error.append(retry);
      emitMatchState(null, "photo", analyticsCode);
      if (safeCode === "rate_limited") {
        setInputMode("text");
        elements.textInput.focus();
      } else {
        retry.focus();
      }
    }

    async function scan(file) {
      if (!file || !elements.photoInput) return null;
      if (elements.reset) elements.reset.hidden = false;
      lastFile = file;
      currentResult = null;
      updateLocation(null);
      scanGeneration += 1;
      const generation = scanGeneration;
      if (currentAbort) currentAbort.abort();
      const abort = new AbortController();
      currentAbort = abort;
      if (currentPhoto && typeof currentPhoto.dispose === "function") currentPhoto.dispose();
      currentPhoto = null;
      elements.preview.removeAttribute("src");
      elements.preview.hidden = true;
      clearError();
      elements.results.replaceChildren();
      root.dataset.state = "scanning";
      setStatus("Reading the watch photo…");
      analytics.emit("band_finder_scan_started", { input_mode: "photo" });

      let photo;
      try {
        photo = await preparePhoto(file);
      } catch (error) {
        if (generation !== scanGeneration) return null;
        renderScanError(error && error.code ? error.code : "invalid_image");
        return null;
      }

      if (generation !== scanGeneration) {
        if (photo && typeof photo.dispose === "function") photo.dispose();
        return null;
      }
      if (currentPhoto && typeof currentPhoto.dispose === "function") currentPhoto.dispose();
      currentPhoto = photo;
      elements.preview.src = photo.previewUrl;
      elements.preview.hidden = false;

      const response = await ocrClient.read(photo.dataUrl, abort.signal);
      if (generation !== scanGeneration) return null;
      currentAbort = null;
      if (!response || !response.ok) {
        renderScanError(response && response.code);
        return null;
      }

      analytics.emit("band_finder_scan_completed", { input_mode: "photo" });
      const result = matcher.match(response.text);
      renderResult(result, "photo", true);
      return result;
    }

    async function share() {
      if (!currentResult || !currentResult.selected) return false;
      const sharedResult = currentResult;
      const sharedMode = inputMode;
      const watch = sharedResult.selected;
      const suffix = serializeShareState(watch);
      if (!suffix) return false;
      const origin = /^https?:\/\//.test(locationApi.origin) ? locationApi.origin : "https://nothingbutbands.com";
      const pathname = /^https?:\/\//.test(locationApi.origin) ? locationApi.pathname : "/pages/watch-band-finder";
      const url = `${origin}${pathname}${suffix}`;
      const payload = {
        title: "Watch band fit",
        text: `Band fit result for ${watchName(watch)}`,
        url,
      };
      try {
        if (typeof navigatorApi.share === "function") await navigatorApi.share(payload);
        else if (navigatorApi.clipboard && typeof navigatorApi.clipboard.writeText === "function") await navigatorApi.clipboard.writeText(url);
        else throw new Error("clipboard unavailable");
      } catch (error) {
        if (currentResult !== sharedResult) return false;
        if (error && error.name === "AbortError") return false;
        let field = elements.results.querySelector("[data-share-link]");
        if (!field) {
          field = makeElement("input", { "data-share-link": true, type: "text", readonly: "", "aria-label": "Result link to copy", className: "wbf-directory-filter" });
          elements.results.append(field);
        }
        field.value = url;
        field.focus();
        field.select();
        setStatus("Copy this link to share your result.");
        return false;
      }
      if (currentResult !== sharedResult) return false;
      analytics.emit("band_finder_result_shared", {
        input_mode: sharedMode,
        outcome: sharedResult.state,
        confidence: sharedResult.confidence,
      });
      setStatus("Result link ready to share.");
      return true;
    }

    function reset() {
      scanGeneration += 1;
      if (currentAbort) currentAbort.abort();
      currentAbort = null;
      if (currentPhoto && typeof currentPhoto.dispose === "function") currentPhoto.dispose();
      currentPhoto = null;
      currentResult = null;
      lastFile = null;
      elements.textInput.value = "";
      if (elements.photoInput) elements.photoInput.value = "";
      if (elements.preview) {
        elements.preview.removeAttribute("src");
        elements.preview.hidden = true;
      }
      elements.results.replaceChildren();
      clearError();
      setStatus("");
      root.dataset.state = "idle";
      if (elements.reset) elements.reset.hidden = false;
      updateLocation(null);
      (inputMode === "photo" ? elements.photoInput : elements.textInput).focus();
    }

    function sharedSlug() {
      const match = /(?:^|[?&])watch=([^&]+)/.exec(String(locationApi.search || ""));
      if (!match) return "";
      try { return decodeURIComponent(match[1].replace(/\+/g, " ")); } catch (_error) { return ""; }
    }

    function mount(nextRoot) {
      root = nextRoot;
      elements = {
        tabs: [...root.querySelectorAll("[data-input-mode]")],
        photoPanel: root.querySelector("[data-photo-panel]"),
        textPanel: root.querySelector("[data-text-panel]"),
        photoInput: root.querySelector("[data-photo-input]"),
        textInput: root.querySelector("[data-text-input]"),
        preview: root.querySelector("[data-photo-preview]"),
        status: root.querySelector("[data-status]"),
        error: root.querySelector("[data-error]"),
        results: root.querySelector("[data-results]"),
        reset: root.querySelector("[data-reset]"),
      };
      root.dataset.state = "idle";
      root.querySelectorAll("button,input").forEach(touchTarget);

      for (const tab of elements.tabs) {
        tab.addEventListener("click", () => setInputMode(tab.dataset.inputMode));
        tab.addEventListener("keydown", (event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          const nextMode = tab.dataset.inputMode === "photo" ? "text" : "photo";
          setInputMode(nextMode, true);
        });
      }
      elements.textInput.addEventListener("input", () => {
        const value = elements.textInput.value.trim();
        if (value) search(value, { inputMode: "text", shouldFocus: false });
        else {
          scanGeneration += 1;
          if (currentAbort) currentAbort.abort();
          currentAbort = null;
          currentResult = null;
          elements.results.replaceChildren();
          root.dataset.state = "idle";
          setStatus("");
          updateLocation(null);
        }
      });
      // Align once on focus, then again if the software keyboard changes the
      // viewport. Never recenter on each keystroke or while browsing results.
      const view = root.ownerDocument.defaultView;
      function alignMobileSearch() {
        if (root.ownerDocument.activeElement === elements.textInput && view?.matchMedia?.('(max-width: 680px)').matches) {
          elements.textInput.scrollIntoView?.({block: 'start', behavior: 'instant'});
        }
      }
      elements.textInput.addEventListener('focus', () => {
        alignMobileSearch();
        view?.visualViewport?.addEventListener('resize', alignMobileSearch);
      });
      elements.textInput.addEventListener('blur', () => {
        view?.visualViewport?.removeEventListener('resize', alignMobileSearch);
      });
      elements.textInput.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowDown" && event.key !== "Enter") return;
        const target = elements.results.querySelector("[data-watch-choice], [data-result-heading]");
        if (target) { event.preventDefault(); target.focus(); }
      });
      if (elements.photoInput) elements.photoInput.addEventListener("change", () => {
        const file = elements.photoInput.files && elements.photoInput.files[0];
        if (file) void scan(file);
      });
      if (elements.reset) elements.reset.addEventListener("click", reset);

      setInputMode("text");
      analytics.emit("band_finder_view", { input_mode: "text" });
      const slug = sharedSlug();
      if (slug) renderResult(matcher.resolveSlug(slug), "shared_url", false);
      return controller;
    }

    const controller = Object.freeze({ mount, setInputMode, search, scan, select: chooseWatch, share, reset });
    return controller;
  }

  const api = { createBandFinderController };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) globalScope.NBBBandFinderController = api;
}(typeof globalThis !== "undefined" ? globalThis : this));


(function bandFinderRuntime(globalScope) {
  "use strict";

  function readJson(document, id, fallback) {
    const node = document.getElementById(id);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); } catch (_error) { return fallback; }
  }

  function createBrowserPhotoAdapter(window) {
    async function decodeImage(file) {
      if (typeof window.createImageBitmap === "function") {
        const bitmap = await window.createImageBitmap(file, { imageOrientation: "from-image" });
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          orientation: 1,
          close() { if (typeof bitmap.close === "function") bitmap.close(); },
        };
      }

      const objectUrl = window.URL.createObjectURL(file);
      const image = new window.Image();
      image.decoding = "async";
      try {
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = objectUrl;
        });
      } catch (error) {
        window.URL.revokeObjectURL(objectUrl);
        throw error;
      }
      return {
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        orientation: 1,
        close() { window.URL.revokeObjectURL(objectUrl); },
      };
    }

    function createCanvas(width, height) {
      const element = window.document.createElement("canvas");
      element.width = width;
      element.height = height;
      const context = element.getContext("2d", { alpha: false });
      return {
        draw(source, dimensions) {
          context.fillStyle = "#FFFFFF";
          context.fillRect(0, 0, width, height);
          context.drawImage(source, 0, 0, dimensions.width, dimensions.height);
        },
        toBlob(type, quality) {
          return new Promise((resolve) => element.toBlob(resolve, type, quality));
        },
      };
    }

    return {
      decodeImage,
      createCanvas,
      createObjectURL(blob) { return window.URL.createObjectURL(blob); },
      revokeObjectURL(url) { window.URL.revokeObjectURL(url); },
    };
  }

  function fixtureFetch(window, fixtures) {
    return async function fetchFixture() {
      const requested = new window.URLSearchParams(window.location.search).get("ocrFixture") || "success";
      const payload = fixtures[requested] || fixtures.success || { ok: false, code: "provider_unavailable" };
      return { async json() { return payload; } };
    };
  }

  function analyticsSink(window) {
    return function publish(event, params) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event, ...params });
    };
  }

  // 2026-10-15: on Shopify the dataset is a theme asset (data-public-data-url),
  // fetched on first search interaction or when the browser is idle, then cached
  // in memory. Local previews still inline it in #wbf-public-data.
  function createDataLoader(window, url) {
    let promise = null;
    let watches = null;
    function attempt() {
      return window.fetch(url, { credentials: "same-origin" }).then((response) => {
        if (!response || !response.ok) throw new Error("watch data unavailable");
        return response.json();
      }).then((data) => {
        if (!Array.isArray(data) || !data.length) throw new Error("watch data invalid");
        return data;
      });
    }
    function load() {
      if (watches) return Promise.resolve(watches);
      if (!promise) {
        promise = attempt()
          .catch(() => new Promise((resolve) => window.setTimeout(resolve, 800)).then(attempt))
          .then((data) => { watches = data; return data; })
          .catch((error) => { promise = null; throw error; });
      }
      return promise;
    }
    return { load, get ready() { return Boolean(watches); } };
  }

  function createDeferredMatcher(matcherApi) {
    let active = matcherApi.createMatcher([]);
    return {
      matcher: {
        match(query) { return active.match(query); },
        select(id) { return active.select(id); },
        resolveSlug(slug) { return active.resolveSlug(slug); },
      },
      use(watches) { active = matcherApi.createMatcher(watches); },
    };
  }

  function lazyDataNotice(document, root) {
    const status = root.querySelector("[data-status]");
    const notice = document.createElement("div");
    notice.className = "wbf-data-state";
    notice.setAttribute("data-data-notice", "");
    notice.setAttribute("role", "status");
    notice.hidden = true;
    if (status && status.parentNode) status.parentNode.insertBefore(notice, status.nextSibling);
    else root.append(notice);
    return {
      loading() {
        notice.replaceChildren(document.createTextNode("Loading the watch list…"));
        notice.hidden = false;
      },
      failed(retry) {
        const message = document.createElement("p");
        message.textContent = "We could not load the watch list. Check your connection and try again.";
        const button = document.createElement("button");
        button.type = "button";
        button.className = "wbf-button wbf-button--secondary wbf-touch-target";
        button.setAttribute("data-data-retry", "");
        button.textContent = "Try again";
        button.addEventListener("click", retry);
        notice.replaceChildren(message, button);
        notice.hidden = false;
      },
      clear() { notice.replaceChildren(); notice.hidden = true; },
    };
  }

  function sharedWatchRequested(window) {
    return /(?:^|[?&])watch=[^&]/.test(String(window.location.search || ""));
  }

  function initialize(window) {
    const document = window.document;
    const root = document.querySelector("[data-band-finder]");
    if (!root) return null;
    const dataHost = document.querySelector("[data-public-data-url]");
    const dataUrl = dataHost ? dataHost.getAttribute("data-public-data-url") : "";
    const inlineData = document.getElementById("wbf-public-data");
    if (!inlineData && dataUrl && typeof window.fetch === "function") return initializeLazy(window, root, dataUrl);
    const watches = readJson(document, "wbf-public-data", []);
    return mountController(window, root, window.NBBBandFinderMatcher && window.NBBBandFinderMatcher.createMatcher(watches));
  }

  function initializeLazy(window, root, dataUrl) {
    const document = window.document;
    const matcherApi = window.NBBBandFinderMatcher;
    if (!matcherApi) return null;
    const deferred = createDeferredMatcher(matcherApi);
    const loader = createDataLoader(window, dataUrl);
    const notice = lazyDataNotice(document, root);
    const input = root.querySelector("[data-text-input]");
    let controller = null;
    let waiting = false;
    root.dataset.dataState = "idle";

    function settle(watches) {
      deferred.use(watches);
      root.dataset.dataState = "ready";
      notice.clear();
      if (!controller) controller = mountController(window, root, deferred.matcher);
      const value = input ? input.value.trim() : "";
      if (controller && waiting && value) controller.search(value, { inputMode: "text", shouldFocus: false });
      waiting = false;
      return controller;
    }

    function ensure(visible) {
      if (loader.ready) return Promise.resolve(controller);
      if (visible) { waiting = true; notice.loading(); }
      root.dataset.dataState = "loading";
      return loader.load().then(settle, () => {
        root.dataset.dataState = "error";
        if (waiting) notice.failed(() => { notice.loading(); void ensure(true); });
        return null;
      });
    }

    if (input) {
      // Registered before the controller's own listener so a keystroke typed while
      // the list is loading waits for the data instead of showing "no match".
      input.addEventListener("input", (event) => {
        if (loader.ready) return;
        event.stopImmediatePropagation();
        void ensure(true);
      });
      input.addEventListener("focus", () => { if (!loader.ready) void ensure(Boolean(input.value.trim())); });
      input.addEventListener("pointerdown", () => { if (!loader.ready) void ensure(false); });
    }

    if (sharedWatchRequested(window)) {
      void ensure(true);
    } else {
      controller = mountController(window, root, deferred.matcher);
      const idle = typeof window.requestIdleCallback === "function"
        ? (callback) => window.requestIdleCallback(callback, { timeout: 4000 })
        : (callback) => window.setTimeout(callback, 2000);
      idle(() => { if (!loader.ready) void ensure(false); });
    }
    window.NBBWatchBandFinderData = { load: () => ensure(false), get ready() { return loader.ready; } };
    return controller;
  }

  function mountController(window, root, matcher) {
    const document = window.document;
    if (!matcher) return null;
    const config = readJson(document, "wbf-config", {});
    const fixtures = readJson(document, "wbf-ocr-fixtures", {});
    const matcherApi = window.NBBBandFinderMatcher;
    const analyticsApi = window.NBBBandFinderAnalytics;
    const photoApi = window.NBBBandFinderPhoto;
    const ocrApi = window.NBBBandFinderOcrClient;
    const controllerApi = window.NBBBandFinderController;
    if (!matcherApi || !analyticsApi || !photoApi || !ocrApi || !controllerApi) return null;

    const fetchImpl = config.ocrMode === "fixture"
      ? fixtureFetch(window, fixtures)
      : window.fetch.bind(window);
    const ocrClient = ocrApi.createOcrClient({
      endpoint: config.ocrEndpoint,
      fetchImpl,
    });
    const photoBrowser = createBrowserPhotoAdapter(window);
    const controller = controllerApi.createBandFinderController({
      matcher,
      serializeShareState: matcherApi.serializeShareState,
      preparePhoto(file) { return photoApi.preparePhoto(file, photoBrowser); },
      ocrClient,
      analytics: analyticsApi.createAnalyticsAdapter(analyticsSink(window)),
      navigator: window.navigator,
      history: window.history,
      location: window.location,
    });
    controller.mount(root);
    window.NBBWatchBandFinder = controller;
    return controller;
  }

  const api = { initialize, createBrowserPhotoAdapter, createDataLoader, createDeferredMatcher };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (globalScope) {
    globalScope.NBBBandFinderRuntime = api;
    if (globalScope.document) initialize(globalScope);
  }
}(typeof globalThis !== "undefined" ? globalThis : this));
